/**
 * The shared contract every leaf-handler group in `./` is written against — the roster domain's
 * event/result types, the level/skill limits, the `ctx.compendium` narrowing, and the immutable
 * pilot/mech/loadout update helpers.
 *
 * Split out of `../reducer`, whose module comment carries the tier's doctrine (the reducer is the
 * wall; each case builds its own inverse; reference-dependent rules read `ctx.compendium`).
 */

import { CommandError, err, ok, type ApplyCtx, type ApplyResult, type Result } from "@lancer-vtt/shared";
import { Compendium } from "@lancer-vtt/compendium";
import type { RosterCommand } from "../commands";
import type { LoadoutData, MechData, PilotData, RosterState } from "../types";

// ── Events ───────────────────────────────────────────────────────────────────

/**
 * What the roster reducer broadcasts after a command. A single coarse
 * `roster_changed` carrying the (id of the) pilot that changed — enough for a
 * client to recompute that pilot's stats from raw state + compendium. The roster
 * domain doesn't yet need fine-grained presence-style events; this is the seam to
 * elaborate (per-mech / per-field events) when the UI wants surgical updates.
 */
export type RosterChangedEvent = {
  type: "roster_changed";
  /** The pilot whose subtree changed (or that was created/deleted). */
  pilotId: string;
}

/**
 * A roster-level ledger (not a pilot) changed: `encounterCommits` or `npcWear`, at `key`. Its
 * own event so a command that names no pilot still reports that it applied (the CLI driver
 * reads an event-less ack as "not applied", and an event-less undo as a no-op).
 */
export type RosterLedgerChangedEvent = {
  type: "roster_ledger_changed";
  ledger: "encounterCommits" | "npcWear";
  key: string;
}

export type RosterEvent = RosterChangedEvent | RosterLedgerChangedEvent;

/** The roster domain's concrete `ApplyResult`. */
export type RosterApplyResult = ApplyResult<RosterState, RosterCommand, RosterEvent>;

// ── Limits ───────────────────────────────────────────────────────────────────

export const MIN_LEVEL = 0;
export const MAX_LEVEL = 12;
export const MIN_SKILL = 0;
export const MAX_SKILL = 6;
// The three `base + level` budgets and the per-key rank ceiling live in `./budgets`
// (the single source the `/roster` UI mirrors as affordances); imported above.

// ── ctx narrowing ──────────────────────────────────────────────────────────────

/**
 * Narrow the loosely-typed `ctx.compendium` (`unknown`, optional) to the real
 * {@link Compendium}. A reference-dependent rule that runs without a compendium
 * is a **programming error** (the call site forgot to inject it) — not a content
 * gap — so we reject with a clear `CommandError` rather than silently treating it
 * as missing content. (A *resolved-but-absent id* is the missing-content case, and
 * that's surfaced per-reference via `MissingContentError` at the read site — a
 * thrown structured error, not this `Result` channel.) Returns a `Result` so the
 * caller threads the rejection like any other expected reducer failure.
 */
export function requireCompendium(ctx: ApplyCtx): Result<Compendium, CommandError> {
  const c = ctx.compendium;
  if (c instanceof Compendium) return ok(c);
  return err(
    new CommandError(
      "roster reducer: a reference-dependent command needs ctx.compendium (a Compendium); none was injected",
    ),
  );
}

// ── Immutable update helpers ───────────────────────────────────────────────────

/** Get a pilot or reject with a `CommandError`. */
export function requirePilot(state: RosterState, pilotId: string): Result<PilotData, CommandError> {
  const p = state.pilots[pilotId];
  if (!p) return err(new CommandError(`unknown pilot ${pilotId}`));
  return ok(p);
}

/** Get a mech within a pilot or reject. */
export function requireMech(pilot: PilotData, mechId: string): Result<MechData, CommandError> {
  const m = pilot.mechs.find((x) => x.id === mechId);
  if (!m) return err(new CommandError(`unknown mech ${mechId} on pilot ${pilot.id}`));
  return ok(m);
}

/** Get a loadout within a mech (by id) or reject. */
export function requireLoadout(mech: MechData, loadoutId: string): Result<LoadoutData, CommandError> {
  const l = mech.loadouts.find((x) => x.id === loadoutId);
  if (!l) return err(new CommandError(`unknown loadout ${loadoutId} on mech ${mech.id}`));
  return ok(l);
}

/** Replace a pilot in the roster, returning new state. */
export function withPilot(state: RosterState, pilot: PilotData): RosterState {
  return { ...state, pilots: { ...state.pilots, [pilot.id]: pilot } };
}

/** Replace a mech within a pilot, returning the new pilot. */
export function withMech(pilot: PilotData, mech: MechData): PilotData {
  return { ...pilot, mechs: pilot.mechs.map((m) => (m.id === mech.id ? mech : m)) };
}

/** Replace a loadout within a mech, returning the new mech. */
export function withLoadout(mech: MechData, loadout: LoadoutData): MechData {
  return {
    ...mech,
    loadouts: mech.loadouts.map((l) => (l.id === loadout.id ? loadout : l)),
  };
}

/** A `roster_changed` event for a pilot id. */
export function changed(pilotId: string): RosterEvent[] {
  return [{ type: "roster_changed", pilotId }];
}
